import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Dialog as MuiDialog, DialogContent, DialogActions } from '@material-ui/core';
import { useFocusTrap } from '../utils/useFocusTrap';
import { useBodyScrollLock } from '../utils/useBodyScrollLock';
import { DialogProps, DIALOG_SIZES } from './tools/Dialog.model';
import { DialogCloseButton } from './components/DialogCloseButton/DialogCloseButton';

import useStyles from './Dialog.style';

const CustomDialog = ({
  scroll = 'paper',
  open = false,
  className,
  closeHandler,
  children,
  showCloseIcon = true,
  disableBackdropClick,
  fullWidth = false,
  dialogContentProps,
  ariaLabelledBy,
  ariaDescribedBy,
  bodyScrollLockDisabled = false,
  disableFocusTrap = false,
  size = DIALOG_SIZES.medium,
  contentRef,
  footer,
  showTopBorder,
  showBottomBorder,
  hasScroll,
  paperInnerClassName,
  contentWrapperClassName,
}: DialogProps) => {
  const c = useStyles({ size, hasScroll });
  const [isBodyScrollLocked, setIsBodyScrollLocked] = useState(open);

  const focusTrapContainerRef = useFocusTrap(
    closeHandler || (() => {}),
    !disableFocusTrap && !!closeHandler
  );

  useEffect(() => {
    if (open) {
      setIsBodyScrollLocked(true);
    }
  }, [open]);

  useBodyScrollLock(bodyScrollLockDisabled || !isBodyScrollLocked);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        const dialogContainer = document.querySelector('.MuiDialog-container');
        if (dialogContainer) {
          dialogContainer.removeAttribute('tabindex');
        }
      }, 0);
    }
  }, [open]);

  return (
    <MuiDialog
      fullWidth={fullWidth}
      open={open}
      scroll={scroll}
      className={clsx(c.root, className)}
      onClose={(_, reason) => {
        if (reason === 'backdropClick' && disableBackdropClick) {
          return;
        }
        closeHandler?.();
      }}
      TransitionProps={{
        onExited: () => setIsBodyScrollLocked(false),
      }}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      disableScrollLock={true}
    >
      <div className={clsx(c.paperInner, paperInnerClassName)} ref={disableFocusTrap ? undefined : focusTrapContainerRef}>
        {showCloseIcon && <DialogCloseButton onClick={closeHandler} />}
        {showTopBorder && <div className={c.topBorder} />}
        <DialogContent {...dialogContentProps} ref={contentRef}>
          <div className={clsx(c.dialogContentWrapper, contentWrapperClassName)}>
            {children}
          </div>
        </DialogContent>
        {footer && (
          <DialogActions>
            <div className={clsx(c.footer, showBottomBorder && c.footerWithBorder)}>
              {footer}
            </div>
          </DialogActions>
        )}
      </div>
    </MuiDialog>
  );
};

export { CustomDialog as Dialog };
export type { DialogProps };
